<template>
    <div>
        <VueDatePicker
            v-model="localValue"
            :uid="uid || undefined"
            :placeholder="placeholder"
            :class="{ 'date-picker-disabled': disabled }"
            :disabled="disabled"
            position="left"
            :enable-time-picker="false"
            :enable-date-picker="true"
            :locale="datePickerLocale.locale"
            :format-locale="datePickerLocale.formatLocale"
            format="MM/dd/yyyy"
            model-type="yyyy-MM-dd"
            :text-input="{
                format: ['MM/dd/yyyy', 'MM-dd-yyyy'],
                enterSubmit: true,
                tabSubmit: true,
                openMenu: 'toggle',
            }"
            :min-date="params?.minDate || null"
            :max-date="params?.maxDate || null"
            :range="params?.rangeMode || false"
            :model-auto="params?.rangeMode || false"
            :teleport="params?.teleport || false"
            :auto-apply="!params?.rangeMode || false"
            :preset-dates="presetDates"
            :multi-calendars="params?.multiCalendars || false"
            @internal-model-change="handleInternal"
        >
            <template
                v-slot:preset-date-range-button="{ label, value, presetDate }"
            >
                <div
                    role="button"
                    :tabindex="0"
                    :class="[
                        'dp__preset_item',
                        'preset-btn',
                        {
                            'preset-btn--active': isPresetActive(value),
                        },
                    ]"
                    @click="handlePresetSelect(value, presetDate)"
                    @keyup.enter.prevent="handlePresetSelect(value, presetDate)"
                    @keyup.space.prevent="handlePresetSelect(value, presetDate)"
                >
                    {{ label }}
                </div>
            </template>
        </VueDatePicker>
        <input
            ref="hiddenInput"
            type="text"
            v-if="required"
            :required="required"
            aria-hidden="true"
            data-focus-proxy="datepicker-input"
            style="
                opacity: 0;
                position: absolute;
                z-index: -1;
                width: 0;
                height: 0;
            "
            :value="localValue"
            @invalid.prevent="handleInvalid"
        />
        <div
            v-if="effectiveInvalid"
            :id="errorId || undefined"
            class="font-note"
            style="color: var(--danger)"
        >
            {{ displayError }}
        </div>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import moment from 'moment';
import { getDatePickerLocale } from '@/i18n/locales';
import { getDefaultDateRangePresets } from '@/utils/dateUtils';

export default {
    name: 'DatePicker',
    components: {
        VueDatePicker,
    },
    setup() {
        const { locale } = useI18n();
        return { locale };
    },
    props: {
        modelValue: {
            type: [String, Date, Array, null],
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'MM/DD/YYYY',
        },
        error: {
            type: String,
            default: undefined,
        },
        externalInvalid: {
            type: Boolean,
            default: false,
        },
        errorId: {
            type: String,
            default: '',
        },
        // Sets the input id to `dp-input-{uid}`, so a <label for> can name it.
        uid: {
            type: String,
            default: '',
        },
        params: {
            // min date, max date
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            isInvalid: false,
            activePresetKey: null,
            isPresetInteraction: false,
        };
    },
    computed: {
        effectiveInvalid() {
            return this.externalInvalid || this.isInvalid;
        },
        displayError() {
            return this.error ?? this.$t('validation.invalidDate');
        },

        datePickerLocale() {
            return getDatePickerLocale(this.locale);
        },
        localValue: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.handleValueChange(value);
            },
        },
        presetDates() {
            const presetConfig = this.params?.presetDates;

            if (!presetConfig) {
                return [];
            }

            if (Array.isArray(presetConfig)) {
                return presetConfig;
            }

            if (typeof presetConfig === 'function') {
                const result = presetConfig(moment);
                return Array.isArray(result) ? result : [];
            }

            return getDefaultDateRangePresets();
        },
    },
    watch: {
        effectiveInvalid() {
            this.$nextTick(this.syncInputAccessibility);
        },
        errorId() {
            this.$nextTick(this.syncInputAccessibility);
        },
    },
    methods: {
        /* Validation */
        handleInvalid() {
            this.isInvalid = true;
            this.$nextTick(this.syncInputAccessibility);
        },
        syncInputAccessibility() {
            const input = this.$el?.querySelector?.('.dp__input');
            if (!input) return;
            if (this.effectiveInvalid) {
                input.setAttribute('aria-invalid', 'true');
                if (this.errorId) {
                    input.setAttribute('aria-describedby', this.errorId);
                }
                return;
            }
            input.removeAttribute('aria-invalid');
            input.removeAttribute('aria-describedby');
        },

        /* Preset Selection */
        handleInternal(date) {
            if (!date) {
                this.activePresetKey = null;
                return;
            }

            if (this.isPresetInteraction) {
                this.isPresetInteraction = false;
            }

            this.updateActivePreset(date);
        },

        handlePresetSelect(value, presetDate) {
            this.isPresetInteraction = true;
            presetDate(value);
        },

        isPresetActive(value) {
            return (
                this.activePresetKey !== null &&
                this.activePresetKey === this.serializePresetValue(value)
            );
        },

        /* Value Management */
        handleValueChange(value) {
            this.isInvalid = false;
            this.updateActivePreset(value);
            this.$emit('update:modelValue', value);
            this.$emit('change', value);
        },

        /* Helpers */
        updateActivePreset(value) {
            this.activePresetKey = this.serializePresetValue(value);
        },

        serializePresetValue(value) {
            if (Array.isArray(value)) {
                const normalized = value
                    .map(this.formatPresetDate)
                    .filter(Boolean);

                return normalized.length ? normalized.join('::') : null;
            }

            return this.formatPresetDate(value) ?? null;
        },

        formatPresetDate(value) {
            if (!value) {
                return null;
            }

            const date = moment(value);
            return date.isValid() ? date.format('MM-DD-YYYY') : null;
        },
    },
    mounted() {
        this.syncInputAccessibility();
    },
    updated() {
        this.syncInputAccessibility();
    },
};
</script>

<style scoped>
:deep(.dp__input) {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1;
    color: var(--black);
    border-radius: var(--radius-default);
    border: 1px solid var(--neutral-500);
    padding-block: var(--space-3);
}

/* Enabled interactions */
:deep(.dp__input_wrap:hover .dp__input:not(:disabled)),
:deep(.dp__input:focus:not(:disabled)) {
    border: 1px solid var(--primary-200);
    outline: none;
}

/*  Disabled interactions */
:deep(.dp__input:disabled) {
    background-color: var(--neutral-500);
    border: none;
    cursor: not-allowed;
}

:deep(.dp__input_wrap:hover .dp__input:disabled),
:deep(.dp__input:focus:disabled) {
    border: none;
    cursor: not-allowed;
    outline: none;
}

:global(.dp__active_date),
:global(.dp__range_end) {
    background-color: var(--success);
    color: var(--white);
}

:global(.dp__menu) {
    border-radius: var(--radius-default);
    border: 1px solid var(--neutral-600);
}

:global(.dp__menu:hover),
:global(.dp__menu:focus-within) {
    border-color: var(--primary-200);
}

:global(.dp__menu:hover .dp__arrow_top),
:global(.dp__menu:focus-within .dp__arrow_top) {
    border-color: var(--primary-200);
}

:global(.dp__today) {
    border: 1px solid var(--success);
}

:global(.date-picker-disabled .dp__input_icon) {
    cursor: not-allowed;
}

:global(.dp__preset_item.dp__preset_active) {
    background-color: var(--primary-200);
    color: var(--white);
}

:global(.dp__action_button.dp__action_select),
:global(.dp__overlay_cell_active) {
    background-color: var(--success);
    color: var(--white);
}

:global(.dp__action_button.dp__action_select:hover) {
    background-color: var(--success);
    color: var(--white);
}

.preset-btn {
    padding-block: var(--space-2);
    padding-inline: var(--space-1);
    border-radius: 4px;
    cursor: pointer;
    color: var(--neutral-900);
    transition: background-color 120ms ease, color 120ms ease;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1;
    text-align: left;
    text-transform: capitalize;
    min-width: fit-content;
    white-space: nowrap;
}

.preset-btn:hover {
    background-color: var(--neutral-500);
}

.preset-btn--active,
.preset-btn--active:hover {
    background-color: var(--success);
    color: var(--white);
}
</style>
